import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Dropdown, Icon, message } from 'components';
import { createFormatDateFunction } from 'utils';
import { useBreakpoint } from 'hooks';
import { getDefaultListingTimezone } from 'api/ListingController';
import { Timezone } from '@bb/domain/dist/types';
import { TourItemContent } from '../types';
import { timeFormatter } from '../utils';
import TimeSelectorDropdownOverlay from './TimeSelectorDropdownOverlay';

export interface TourShowingTimeSelectorProps {
  item: TourItemContent;
}

// TODO: APP-5470 - select time for a listing
const TourShowingTimeSelector: React.FC<TourShowingTimeSelectorProps> = ({
  item: {
    time,
    timeEnd,
    timezone,
    status,
    appointmentId,
    originalTime,
    originalTimeEnd,
    listingId,
    id,
  },
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const triggerRef = useRef(null);
  const [officeTimezone, setOfficeTimezone] = useState<Timezone | undefined>();

  // assume no appointmentId indicates draft status
  const selectionIsDisabled = Boolean(appointmentId);

  const toggleDropdownVisibility = () => {
    if (!selectionIsDisabled) {
      setDropdownVisible(curState => !curState);
    }
  };

  let startTime = timeFormatter(time, timezone);
  let endTime = timeFormatter(timeEnd, timezone);

  if (status === 'time_change' && originalTime && originalTimeEnd) {
    startTime = timeFormatter(originalTime, timezone);
    endTime = timeFormatter(originalTimeEnd, timezone);
  }

  useEffect(() => {
    const fetchDefaultTimezone = async () => {
      try {
        const defaultTimezone = await getDefaultListingTimezone({
          listingId,
        });
        setOfficeTimezone(defaultTimezone);
      } catch (err) {
        message.error('There was an error retrieving timezones');
      }
    };
    if (!timezone && !officeTimezone) {
      fetchDefaultTimezone();
    }
    startTime = timeFormatter(time, officeTimezone);
    endTime = timeFormatter(timeEnd, officeTimezone);
    if (
      status === 'time_change' &&
      originalTime &&
      originalTimeEnd &&
      officeTimezone
    ) {
      startTime = timeFormatter(originalTime, officeTimezone);
      endTime = timeFormatter(originalTimeEnd, officeTimezone);
    }
  }, [officeTimezone]);

  return (
    <Dropdown
      getPopupContainer={trigger => document.body}
      open={dropdownVisible}
      placement="bottomLeft"
      trigger={['click']}
      dropdownRender={() =>
        // Logic to be changed once antd version is updated to recent version with destroyOnPopupHide method
        dropdownVisible ? (
          <TimeSelectorDropdownOverlay
            listingId={listingId || ''}
            listingTimezone={timezone || officeTimezone}
            startTime={time}
            endTime={timeEnd}
            itemId={id}
            toggleDropdownVisibility={toggleDropdownVisibility}
            setDropdownVisible={setDropdownVisible}
            triggerRef={triggerRef}
          />
        ) : (
          <></>
        )
      }
      disabled={selectionIsDisabled}
    >
      <StyledTrigger
        $disabled={selectionIsDisabled}
        onClick={toggleDropdownVisibility}
        ref={triggerRef}
      >
        {time && timeEnd ? `${startTime} - ${endTime}` : 'Select times'}
        {!selectionIsDisabled && (
          <Icon type="arrowDownThin" width={9} offset={[5, -1]} color="info" />
        )}
      </StyledTrigger>
    </Dropdown>
  );
};

export default TourShowingTimeSelector;

const StyledTrigger = styled.div<{ $disabled: boolean }>`
  ${({ $disabled }) =>
    $disabled
      ? 'color: rgba(0, 0, 0, 0.65);'
      : 'color: #5c93fd; cursor: pointer;'};
`;
